import {
  Component,
  ElementRef,
  InputSignal,
  Signal,
  effect,
  inject,
  input,
  viewChildren,
} from '@angular/core';
import { SettingsService, BlackColorsRef, Coordinate } from '@kinomap-dev/ng-ui-lib/core';
import { CreateContentService } from '../../services/create-content.service';
import { ContentType } from '../../pages/content-creation/content-creation';
import { BehaviorSubject } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { ColorService } from '../../services/color.service';
import { SyncroStatus } from '../syncro-status/syncro-status';
import { ModalService } from '../../services/modal-service';
import { FullscreenSyncroModal } from '../fullscreen-syncro-modal/fullscreen-syncro-modal';
import { MediaBlockService } from '../../services/media-block-service';
import { VideoUploader } from '../video-uploader/video-uploader';
import { GpsUploader } from '../gps-uploader/gps-uploader';
import { SyncroEditor } from '../syncro-editor/syncro-editor';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { PlayerService } from '@kinomap-dev/ng-ui-lib/player';
import { AutoSyncService } from '../../services/auto-sync';
import { FullscreenGpsModal } from '../fullscreen-gps-modal/fullscreen-gps-modal';
import { FullscreenCoachingIntervalsModal } from '../fullscreen-coaching-intervals-modal/fullscreen-coaching-intervals-modal';

import { TranslateModule } from '@ngx-translate/core';
import { InputRadioElement } from '../input-radio-element/input-radio-element';
import { InputRadioElementItem } from '../../types/input-radio-element-item.type';
import { WorkoutSourceType } from '../../types/workout.type';
import { WorkoutUploader } from '../workout-uploader/workout-uploader';

const workoutSourceItemList: InputRadioElementItem<WorkoutSourceType>[] = [
  {
    value: 'create',
    title: 'media_block.workout_source.create',
    subTitle: 'media_block.workout_source.create_hint',
    icon: 'edit',
    default: true,
  },
  {
    value: 'import',
    title: 'media_block.workout_source.import',
    subTitle: 'media_block.workout_source.import_hint',
    icon: 'upload',
  },
];

@Component({
  selector: 'app-media-block',
  imports: [
    TranslateModule,
    SyncroStatus,
    VideoUploader,
    GpsUploader,
    IconComponent,
    SyncroEditor,
    InputRadioElement,
    WorkoutUploader,
  ],
  templateUrl: './media-block.html',
  styleUrl: './media-block.scss',
})
export class MediaBlock {
  public readonly colorService: ColorService = inject(ColorService);
  public readonly settingService: SettingsService = inject(SettingsService);
  readonly contentType: InputSignal<ContentType> = input.required<ContentType>();
  readonly mediaForm: InputSignal<FormGroup | undefined> = input<FormGroup>();
  readonly workoutForm: InputSignal<FormGroup | undefined> = input<FormGroup>();
  protected readonly workoutSourceItemList: InputRadioElementItem<WorkoutSourceType>[] =
    workoutSourceItemList;
  readonly createContentService: CreateContentService = inject(CreateContentService);
  readonly playerService: PlayerService = inject(PlayerService);
  private modalService: ModalService = inject(ModalService);
  public mediaBlockService: MediaBlockService = inject(MediaBlockService);
  public autoSyncService: AutoSyncService = inject(AutoSyncService);

  metric: string = this.settingService.unit === 'imperial' ? 'ft' : 'm';
  chartColor: string = BlackColorsRef.black_050;
  rawCoordinates$: BehaviorSubject<Coordinate[]> = new BehaviorSubject<Coordinate[]>(
    this.mediaBlockService.rawCorrdinates(),
  );
  inputsVideoRefs: Signal<readonly ElementRef<HTMLInputElement>[]> = viewChildren('videoInput', {
    read: ElementRef<HTMLInputElement>,
  });

  constructor() {
    effect(() => {
      const mediaForm: FormGroup | undefined = this.mediaForm();
      if (!mediaForm) return;
      this.mediaBlockService.mediaForm = mediaForm;
      this.mediaBlockService.contentType = this.contentType();
    });

    effect(() => {
      if (this.mediaBlockService.hasVideoAndGps()) {
        this.autoSyncService.controlIfMediaCanBeSynchronised();
      }
    });
  }

  openFullscreenModal(): void {
    if (this.contentType() === 'scenic') {
      this.modalService.openModal<FullscreenSyncroModal>(
        FullscreenSyncroModal,
        '100%',
        '100vh',
        'media',
      );
    } else if (this.contentType() === 'gps-only') {
      this.modalService.openModal<FullscreenGpsModal>(
        FullscreenGpsModal,
        '100%',
        '100vh',
        'media',
        undefined,
        {},
      );
    } else if (this.contentType() === 'coaching') {
      this.modalService.openModal<FullscreenCoachingIntervalsModal>(
        FullscreenCoachingIntervalsModal,
        '100%',
        '100vh',
        'media',
        undefined,
        {},
      );
    }
  }
}
